import PropTypes from 'prop-types';
import { useFormContext } from 'react-hook-form';
import usePricingsDisplayer from '../hooks/usePricingsDisplayer.js';
import {
  any,
  assoc,
  compose,
  eqProps,
  equals,
  F,
  filter,
  find,
  findIndex,
  includes,
  isEmpty,
  length,
  map,
  modify,
  pluck,
  prop,
  propOr,
  slice,
  split,
  toString
} from 'ramda';
import { mapIndexed } from '@@serverHelpers/index.mjs';
import React, { useEffect, useMemo, useState } from 'react';
import { Icon, Message, PricingCard, QuoteTable } from '@fasstech/middle-design-system';

const PricingsDisplayer = ({
  getMiddleText,
  maxNumberOfProducts,
  setShowDocumentError,
  setValue,
  offerPerPage,
  currentBeneficiaryIndex,
  selectedCommissionRate,
  viewMode,
  currentPricings
}) => {
  const [page, setPage] = useState(0);
  const { watch } = useFormContext();

  const packs = watch('packs');
  const atLeastOnePricingIsRecommended = compose(any(propOr(false, 'isRecommended')))(
    currentPricings
  );

  const maxPage = useMemo(
    () => Math.ceil(length(currentPricings) / offerPerPage) - 1,
    [currentPricings, offerPerPage]
  );
  const visiblePackIds = useMemo(
    () =>
      compose(
        pluck('moduleCode'),
        slice(page * offerPerPage, (page + 1) * offerPerPage)
      )(currentPricings),
    [currentPricings, page, offerPerPage]
  );

  useEffect(() => {
    setPage(0);
  }, [currentBeneficiaryIndex]);

  useEffect(() => {
    if (isEmpty(visiblePackIds) && page > 0) {
      return setPage(page - 1);
    }
  }, [visiblePackIds]);

  const displayPricingsPagination = length(currentPricings) > offerPerPage;

  const {
    displaySupplements,
    setDisplaySupplements,
    displayError,
    downloadNotice,
    downloadDocuments,
    toggleIsSelected,
    dynamicColumns,
    getTotalPrice,
    totalSelectedPricings,
    getPriceByCommission,
    round
  } = usePricingsDisplayer({
    currentPricings,
    setValue,
    setShowDocumentError,
    maxNumberOfProducts,
    watch,
    visiblePackIds,
    currentBeneficiaryIndex,
    selectedCommissionRate,
    packs,
    viewMode
  });

  if (currentPricings) {
    return (
      <div
        className={`${atLeastOnePricingIsRecommended ? 'mt-13' : 'mt-5'} grid grid-cols-1 md:grid-cols-3 gap-y-4`}>
        <div className={'relative col-span-3 mx-auto'}>
          {displayPricingsPagination && (
            <div
              className={`${atLeastOnePricingIsRecommended ? 'top-[-70px]' : 'top-[-40px]'} hidden md:flex absolute right-0 gap-2`}>
              <Icon
                name={page === 0 ? 'circle_left_chevron_inactive' : 'circle_left_chevron'}
                className={`${page === 0 ? 'pointer-events-none' : 'cursor-pointer'}`}
                onClick={() => setPage(page === 0 ? page : page - 1)}
                size={35}
              />
              <Icon
                className={`${page === maxPage ? 'pointer-events-none' : 'cursor-pointer'}`}
                name={page === maxPage ? 'circle_right_chevron_inactive' : 'circle_right_chevron'}
                onClick={() => setPage(page === maxPage ? page : page + 1)}
                size={35}
              />
            </div>
          )}

          <div className={`grid grid-cols-1 ${dynamicColumns} gap-12 md:gap-4`}>
            {mapIndexed((pricing) => {
              const filteredPricing = compose(
                assoc('initialPrice', getPriceByCommission(pricing).initialPrice || 0),
                assoc('defaultPrice', getPriceByCommission(pricing).defaultPrice || 0)
              )(pricing);

              const pricingIndex = findIndex(eqProps('moduleCode', pricing), currentPricings);

              const annualPriceLabel = Math.round(getTotalPrice(pricing) * 12 * 100) / 100;

              const currentSupplementaryCovers = watch(
                `packs.${currentBeneficiaryIndex}.pricings.${pricingIndex}.supplementaryCovers`
              );

              const isUnique = (cover) =>
                compose(includes('UNIQUE'), propOr('', 'supplementarySelectionMode'))(cover);

              const setSupplementaryCoverIsSelected = (supplementaryCover, index) => {
                const {
                  moduleLabel,
                  isPreSelected
                } = supplementaryCover;

                const allSelected = filter((c) => c.isPreSelected, pricingSupplementaryCovers);

                const uniqueSelected = find(isUnique, allSelected);

                if (isUnique(supplementaryCover)) {
                  if (uniqueSelected && uniqueSelected.moduleLabel !== moduleLabel) {
                    setValue(
                      `packs.${currentBeneficiaryIndex}.pricings.${pricingIndex}.supplementaryCovers`,
                      pricingSupplementaryCovers.map((cover) =>
                        isUnique(cover)
                          ? {
                            ...cover,
                            isPreSelected:
                              cover.moduleLabel === moduleLabel ? !isPreSelected : false
                          }
                          : cover
                      )
                    );
                    return;
                  }
                  setValue(
                    `packs.${currentBeneficiaryIndex}.pricings.${pricingIndex}.supplementaryCovers.${index}.isPreSelected`,
                    !isPreSelected
                  );
                } else {
                  setValue(
                    `packs.${currentBeneficiaryIndex}.pricings.${pricingIndex}.supplementaryCovers.${index}.isPreSelected`,
                    !isPreSelected
                  );
                }
              };

              const supplementaryCoverIsRequired = compose(
                includes('OBLIGATOIRE'),
                split('_'),
                propOr('', 'supplementarySelectionMode')
              )(pricing);

              const pricingSupplementaryCovers = propOr([], 'supplementaryCovers', pricing);

              const unselectAllSupplementaryCover = () => {
                const supplementaryCoversToUpdate = map(
                  modify('isPreSelected', F),
                  pricingSupplementaryCovers
                );
                setValue(
                  `packs.${currentBeneficiaryIndex}.pricings.${pricingIndex}.supplementaryCovers`,
                  supplementaryCoversToUpdate
                );
              };

              return (
                <PricingCard
                  key={filteredPricing.moduleCode}
                  supplementaryCoversTitle={getMiddleText(
                    'NIVEAUXGARANTIESETTARIFS_GARANTIES_SURCO_LABEL'
                  )}
                  moduleLabel={filteredPricing.moduleLabel}
                  isSelected={filteredPricing.isPreSelected}
                  isRecommended={filteredPricing.isRecommended}
                  pricingIndex={pricingIndex}
                  pricing={filteredPricing}
                  displaySupplements={displaySupplements}
                  setDisplaySupplements={setDisplaySupplements}
                  onClickDownloadPricingNotice={downloadDocuments}
                  onClickDownloadSupplementNotice={downloadDocuments}
                  onSelectPricing={() => toggleIsSelected(pricing)}
                  isDisplayed={
                    offerPerPage === -1
                      ? true
                      : includes(prop('moduleCode', pricing), visiblePackIds)
                  }
                  downloadingNotice={(moduleCode) =>
                    downloadNotice.downloading && downloadNotice.moduleCode === moduleCode
                  }
                  supplementaryCovers={currentSupplementaryCovers}
                  getSupplementaryCoverPrice={(supplementaryCover) =>
                    getPriceByCommission(supplementaryCover).defaultPrice || 0
                  }
                  getInitialPriceByCommission={(supplementaryCover) =>
                    getPriceByCommission(supplementaryCover).initialPrice
                  }
                  noSupplementLabel={getMiddleText(
                    'NIVEAUXGARANTIESETTARIFS_GARANTIES_NO_SUPPLEMENTARY_COVER'
                  )}
                  onSelectNoSupplement={unselectAllSupplementaryCover}
                  displayNoSupplementButton={supplementaryCoverIsRequired}
                  totalPrice={getTotalPrice(pricing)}
                  annualTotalPrice={annualPriceLabel}
                  onSelectSupplement={(supplementaryCover, index) =>
                    setSupplementaryCoverIsSelected(supplementaryCover, index)
                  }
                  selectedButtonLabel={getMiddleText(
                    'NIVEAUXGARANTIESETTARIFS_GENERAL_OFFER_SELECTED_BUTTON'
                  )}
                  selectButtonLabel={getMiddleText(
                    'NIVEAUXGARANTIESETTARIFS_GENERAL_SELECT_OFFER_BUTTON'
                  )}
                  downloadTooltipText={getMiddleText(
                    'NIVEAUXGARANTIESETTARIFS_GARANTIES_TELECH_INFO_BULLE'
                  )}
                />
              );
            }, currentPricings)}
            {displayError && (
              <Message
                variant="error"
                label={getMiddleText(
                  'NIVEAUXGARANTIESETTARIFS_GENERAL_MAX_NUMBER_OF_PRODUCTS_ERROR',
                  { maxNumberOfProducts }
                )}
              />
            )}
          </div>
        </div>
        <div className={'col-span-3 md:col-span-2 md:col-end-4'}>
          <QuoteTable
            title={'Montant total de cotisation'}
            iconName={'calculatorFilled'}
            rows={[
              {
                label: 'Montant cotisation',
                monthlyAmount: toString(round(totalSelectedPricings) || 0),
                annualAmount: toString(round(totalSelectedPricings * 12) || 0)
              }
            ]}
            isDisplayed={equals(viewMode, 'CUSTOMER_GRANTEES')}
          />
        </div>
      </div>
    );
  } else {
    return <div>{'Aucune garantie disponible'}</div>;
  }
};

PricingsDisplayer.propTypes = {
  selectedOffers: PropTypes.array,
  setSelectedOffers: PropTypes.func
};

export default PricingsDisplayer;
